/**
 * Room Program — Pre-design Research "Documents" tool (docs/ROOM_PROGRAM.md).
 *
 * Opens via the milestoneDocumentProviders.js 'programbuilder' row's "Open Room Program" action,
 * which fires `open-program-builder-modal` ({ msId, projectId, projectName }) — same launch
 * mechanism as Site Analysis (SiteAnalysisModal.jsx).
 *
 * A fast-entry front end onto the REAL Concept -> Unit -> Level -> Room hierarchy (Rooms and
 * Spaces / Concept Units / Concept Levels / Project Concepts) — everything here reads/writes
 * through services/programBuilder.js, which itself wraps services/design.js's existing
 * functions. No separate "preliminary" data model.
 *
 * The tool's OWN starting screen (not a gate before it opens) is "create and activate a concept
 * first" when the project has no Selected Concept yet — reuses the existing AddConceptModal
 * (`open-add-concept-modal` event) and swaps into the real editor the instant a concept becomes
 * selected (listens for `concepts-updated` / `outline-render`).
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import {
  getConcepts, getSelectedConcept, setSelectedConcept,
  getRoomProgram, createConceptUnit, deleteConceptUnitCascade,
  createConceptLevel, updateConceptLevel, deleteConceptLevelCascade,
  createRoom, updateRoom, deleteRoom, moveRoomToLevel,
  listSuggestions, reviewSuggestion,
} from '../services/programBuilder.js'
import { confirmDialog, promptDialog, alertDialog } from './Dialog.jsx'
import { UNIT_TYPE_OPTIONS } from '../outline/constants.js'

const ROOM_TYPE_PLACEHOLDER = 'Room type…'
const SCOPE_TYPES = ['New Construction', 'Cosmetic Refresh', 'Light Alteration', 'Moderate Alteration', 'Heavy Alteration', 'Major Alteration', 'Existing to Remain']

/* ── Host — mounted once in OutlineView ──────────────────────────────────────────────────── */
export default function ProgramBuilderHost() {
  const [launch, setLaunch] = useState(null)
  useEffect(() => {
    const h = e => setLaunch(e.detail || null)
    window.addEventListener('open-program-builder-modal', h)
    return () => window.removeEventListener('open-program-builder-modal', h)
  }, [])
  if (!launch) return null
  return <ProgramBuilderModal key={launch.msId} {...launch} onClose={() => setLaunch(null)} />
}

/* ── The tool ─────────────────────────────────────────────────────────────────────────────── */
function ProgramBuilderModal({ projectId, projectName, onClose }) {
  const [phase, setPhase] = useState('loading')   // 'loading' | 'no-concept' | 'ready'
  const [candidates, setCandidates] = useState([])
  const [concept, setConcept] = useState(null)
  const [program, setProgram] = useState(null)     // { units, templates }
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState('rooms')          // 'rooms' | 'suggestions'
  const [suggestions, setSuggestions] = useState(null)
  const [dragRoom, setDragRoom] = useState(null)    // { roomId } while dragging over a level
  const [addingUnit, setAddingUnit] = useState(false)
  const loadingRef = useRef(false)

  const load = useCallback(async () => {
    if (loadingRef.current) return
    loadingRef.current = true
    setError(null)
    try {
      const selected = await getSelectedConcept(projectId)
      if (!selected) {
        const all = await getConcepts(projectId)
        setCandidates(all || [])
        setConcept(null)
        setPhase('no-concept')
        return
      }
      setConcept(selected)
      const prog = await getRoomProgram(selected.id)
      setProgram(prog)
      setPhase('ready')
    } catch (ex) {
      setError(ex.message || 'Failed to load')
      setPhase('no-concept')
    } finally {
      loadingRef.current = false
    }
  }, [projectId])

  useEffect(() => { load() }, [load])

  // Swap into the real editor the instant a concept is created/selected elsewhere (AddConceptModal
  // dispatches both of these) — no need to close and reopen this modal.
  useEffect(() => {
    function onChange() { if (phase === 'no-concept') load() }
    window.addEventListener('concepts-updated', onChange)
    window.addEventListener('outline-render', onChange)
    return () => { window.removeEventListener('concepts-updated', onChange); window.removeEventListener('outline-render', onChange) }
  }, [phase, load])

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  async function refreshProgram() {
    if (!concept) return
    const prog = await getRoomProgram(concept.id)
    setProgram(prog)
  }

  async function loadSuggestions() {
    try { setSuggestions(await listSuggestions(projectId)) }
    catch (ex) { setSuggestions([]); console.error(ex) }
  }
  useEffect(() => { if (tab === 'suggestions') loadSuggestions() }, [tab])   // eslint-disable-line react-hooks/exhaustive-deps
  const pendingCount = (suggestions || []).filter(s => s.status === 'pending').length

  async function handleUseConcept(id) {
    setBusy(true)
    try { await setSelectedConcept(projectId, id); await load() }
    catch (ex) { setError(ex.message) }
    finally { setBusy(false) }
  }

  async function handleAddUnit(unitType) {
    if (!concept) return
    setAddingUnit(false)
    try { await createConceptUnit(concept.id, unitType); await refreshProgram() }
    catch (ex) { await alertDialog({ title: 'Could not add unit', message: ex.message }) }
  }

  async function handleDeleteUnit(unit) {
    const levelCount = unit.levels.length
    const roomCount = unit.levels.reduce((n, l) => n + l.rooms.length, 0)
    if (!(await confirmDialog({
      title: 'Delete Unit?',
      message: (levelCount || roomCount)
        ? `"${unit.name}" and its ${levelCount} level${levelCount === 1 ? '' : 's'} (${roomCount} room${roomCount === 1 ? '' : 's'}) will be deleted.`
        : `Delete "${unit.name}"?`,
      confirmLabel: 'Delete Unit', danger: true,
    }))) return
    try { await deleteConceptUnitCascade(unit.id); await refreshProgram() }
    catch (ex) { await alertDialog({ title: 'Could not delete unit', message: ex.message }) }
  }

  async function handleAddLevel(unitId) {
    if (!program) return
    const unit = program.units.find(u => u.id === unitId)
    const name = await promptDialog({ title: 'Add Level', placeholder: 'e.g. Main Level, Upper Level, Garage/ADU' })
    if (!name) return
    const stackingOrder = (unit?.levels?.length || 0)
    try { await createConceptLevel(unitId, { Name: name, 'Stacking Order': stackingOrder }); await refreshProgram() }
    catch (ex) { await alertDialog({ title: 'Could not add level', message: ex.message }) }
  }

  async function handleRenameLevel(level) {
    const name = await promptDialog({ title: 'Rename Level', defaultValue: level.name })
    if (!name || name === level.name) return
    try { await updateConceptLevel(level.id, { Name: name }); await refreshProgram() }
    catch (ex) { await alertDialog({ title: 'Could not rename level', message: ex.message }) }
  }

  async function handleDeleteLevel(level) {
    const count = level.rooms.length
    if (!(await confirmDialog({
      title: 'Delete Level?',
      message: count ? `"${level.name}" and its ${count} room${count === 1 ? '' : 's'} will be deleted.` : `Delete "${level.name}"?`,
      confirmLabel: 'Delete Level', danger: true,
    }))) return
    try { await deleteConceptLevelCascade(level.id); await refreshProgram() }
    catch (ex) { await alertDialog({ title: 'Could not delete level', message: ex.message }) }
  }

  async function handleAddRoom(level) {
    try {
      await createRoom(level.id, concept.id, level.name, { 'Custom Name': 'New Room', 'Scope Type': 'New Construction' })
      await refreshProgram()
    } catch (ex) { await alertDialog({ title: 'Could not add room', message: ex.message }) }
  }

  async function handleUpdateRoom(room, fields) {
    try { await updateRoom(room.id, fields); await refreshProgram() }
    catch (ex) { await alertDialog({ title: 'Could not update room', message: ex.message }) }
  }

  async function handleDeleteRoom(room) {
    if (!(await confirmDialog({ title: 'Delete Room?', message: `Delete "${room.name || 'this room'}"?`, confirmLabel: 'Delete Room', danger: true }))) return
    try { await deleteRoom(room.id); await refreshProgram() }
    catch (ex) { await alertDialog({ title: 'Could not delete room', message: ex.message }) }
  }

  async function handleMoveRoom(roomId, targetLevelId, targetLevelName) {
    if (!roomId || !targetLevelId) return
    try { await moveRoomToLevel(roomId, targetLevelId, targetLevelName); await refreshProgram() }
    catch (ex) { await alertDialog({ title: 'Could not move room', message: ex.message }) }
  }

  async function handleReview(s, decision) {
    let note = null
    if (decision === 'reject') note = await promptDialog({ title: 'Reject suggestion', placeholder: 'Optional note', multiline: true })
    setBusy(true)
    try { await reviewSuggestion(s.id, decision, note); await loadSuggestions(); await refreshProgram() }
    catch (ex) { await alertDialog({ title: 'Could not review suggestion', message: ex.message }) }
    finally { setBusy(false) }
  }

  return (
    // zIndex kept below AddConceptModal's 600 (frontend/src/components/AddConceptModal.jsx) — the
    // "no concept yet" starting screen fires that modal on top of this one, and it must win the
    // stack (Matt, 2026-09-17: it was rendering behind this modal instead).
    <div style={{ position: 'fixed', inset: 0, zIndex: 590, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{
        background: 'var(--aj-bg-elevated)', width: 'min(880px, 94vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        borderRadius: 8, border: '0.5px solid var(--aj-border)', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
      }}>
        <div style={{ background: '#07272D', color: '#fff', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: '"Montserrat",sans-serif', fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Room Program</div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, opacity: 0.75 }}>{projectName || ''}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {phase === 'loading' && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif', fontSize: 13 }}>Loading…</div>
        )}

        {phase === 'no-concept' && (
          <div style={{ padding: '40px 32px', textAlign: 'center' }}>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 14, color: 'var(--aj-text)', marginBottom: 8, fontWeight: 600 }}>
              Create and activate a concept first to use this document builder
            </div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text-muted)', marginBottom: 22, maxWidth: 440, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
              The Room Program lives on the project's active Concept, the same Units/Levels/Rooms
              used everywhere else in Cortex. {candidates.length > 0
                ? 'This project has concepts already — pick one to activate, or create a new one.'
                : 'This project doesn’t have one yet.'}
            </div>
            {candidates.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 360, margin: '0 auto 18px' }}>
                {candidates.map(c => (
                  <button key={c.id} disabled={busy} onClick={() => handleUseConcept(c.id)}
                    style={{ padding: '8px 12px', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)', textAlign: 'left' }}>
                    Use "{c.fields?.['Parti Pris Title'] || 'Concept'}"
                  </button>
                ))}
              </div>
            )}
            {error && <div style={{ color: '#c0392b', fontSize: 11.5, marginBottom: 12 }}>{error}</div>}
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('open-add-concept-modal', { detail: { projectId } }))}
              style={{ padding: '9px 20px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, background: '#07272D', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
              Create a concept
            </button>
          </div>
        )}

        {phase === 'ready' && program && (
          <>
            <div style={{ display: 'flex', gap: 2, padding: '10px 20px 0', borderBottom: '0.5px solid var(--aj-border)' }}>
              {['rooms', 'suggestions'].map(t => (
                <button key={t} onClick={() => setTab(t)} style={{
                  padding: '8px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                  background: 'none', border: 'none', borderBottom: tab === t ? '2px solid #07272D' : '2px solid transparent',
                  color: tab === t ? 'var(--aj-text)' : 'var(--aj-text-muted)',
                }}>
                  {t === 'rooms' ? 'Rooms' : `Suggestions${pendingCount ? ' (' + pendingCount + ')' : ''}`}
                </button>
              ))}
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>
              {tab === 'rooms' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--aj-text-muted)' }}>
                    {program.units.length} unit{program.units.length === 1 ? '' : 's'}
                  </span>
                  {!addingUnit
                    ? <button onClick={() => setAddingUnit(true)} style={btnGhost}>+ Add Unit</button>
                    : (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <select autoFocus defaultValue="" onChange={e => e.target.value && handleAddUnit(e.target.value)} style={inputStyle}>
                          <option value="" disabled>Choose a unit type…</option>
                          {UNIT_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <button onClick={() => setAddingUnit(false)} style={btnGhost}>Cancel</button>
                      </div>
                    )}
                </div>
              )}
              {tab === 'rooms' && program.units.length === 0 && !addingUnit && (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--aj-text-muted)', fontSize: 12.5 }}>
                  This concept doesn't have any units yet — add one to start the room program.
                </div>
              )}
              {tab === 'rooms' && program.units.map(unit => (
                <div key={unit.id} style={{ marginBottom: 22 }}>
                  <div style={{ fontFamily: '"Montserrat",sans-serif', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginBottom: 8 }}>
                    {unit.name}
                    <button onClick={() => handleAddLevel(unit.id)} style={{ marginLeft: 10, fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0, background: 'none', border: 'none', color: '#1a6fa8', cursor: 'pointer' }}>+ Add level</button>
                    <button onClick={() => handleDeleteUnit(unit)} style={{ marginLeft: 10, fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0, background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer' }}>Delete unit</button>
                  </div>
                  {unit.levels.length === 0 && <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', fontStyle: 'italic', marginBottom: 8 }}>No levels yet.</div>}
                  {unit.levels.map(level => (
                    <LevelBlock
                      key={level.id} level={level} templates={program.templates}
                      dragRoom={dragRoom} setDragRoom={setDragRoom}
                      onRename={() => handleRenameLevel(level)}
                      onDelete={() => handleDeleteLevel(level)}
                      onAddRoom={() => handleAddRoom(level)}
                      onUpdateRoom={handleUpdateRoom}
                      onDeleteRoom={handleDeleteRoom}
                      onDropRoom={roomId => handleMoveRoom(roomId, level.id, level.name)}
                    />
                  ))}
                </div>
              ))}

              {tab === 'suggestions' && (
                <SuggestionsPanel suggestions={suggestions} busy={busy} onReview={handleReview} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function LevelBlock({ level, templates, dragRoom, setDragRoom, onRename, onDelete, onAddRoom, onUpdateRoom, onDeleteRoom, onDropRoom }) {
  const [dragOver, setDragOver] = useState(false)
  return (
    <div
      onDragOver={e => { if (dragRoom) { e.preventDefault(); setDragOver(true) } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => { e.preventDefault(); setDragOver(false); if (dragRoom) onDropRoom(dragRoom.roomId); setDragRoom(null) }}
      style={{
        border: '0.5px solid ' + (dragOver ? '#1a6fa8' : 'var(--aj-border)'), borderRadius: 6, marginBottom: 10,
        background: dragOver ? 'rgba(26,111,168,0.06)' : 'var(--aj-bg-surface)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '0.5px solid var(--aj-border)' }}>
        <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', flex: 1 }}>{level.name}</span>
        <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)' }}>{level.rooms.length} room{level.rooms.length === 1 ? '' : 's'}</span>
        <button onClick={onRename} style={btnGhost}>Rename</button>
        <button onClick={onAddRoom} style={btnGhost}>+ Room</button>
        <button onClick={onDelete} style={{ ...btnGhost, color: '#c0392b' }}>Delete</button>
      </div>
      <div>
        {level.rooms.length === 0 && <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>Drag a room here, or add one.</div>}
        {level.rooms.map(room => (
          <RoomRow key={room.id} room={room} templates={templates} setDragRoom={setDragRoom}
            onUpdate={fields => onUpdateRoom(room, fields)} onDelete={() => onDeleteRoom(room)} />
        ))}
      </div>
    </div>
  )
}

const btnGhost = { padding: '3px 8px', fontFamily: '"DM Sans",sans-serif', fontSize: 11, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text-mid)' }
const inputStyle = { padding: '4px 6px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)' }

function RoomRow({ room, templates, setDragRoom, onUpdate, onDelete }) {
  const [name, setName] = useState(room.name)
  const [area, setArea] = useState(room.areaManual ?? '')
  const [notes, setNotes] = useState(room.notes)
  useEffect(() => { setName(room.name); setArea(room.areaManual ?? ''); setNotes(room.notes) }, [room.id, room.name, room.areaManual, room.notes])

  function commitName() { if (name !== room.name) onUpdate({ 'Custom Name': name }) }
  function commitArea() { const n = area === '' ? null : Number(area); if (n !== room.areaManual) onUpdate({ 'Area Manual': n }) }
  function commitNotes() { if (notes !== room.notes) onUpdate({ 'Room Notes': notes }) }

  return (
    <div
      draggable
      onDragStart={() => setDragRoom({ roomId: room.id })}
      onDragEnd={() => setDragRoom(null)}
      style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 90px 1.2fr auto', gap: 8, alignItems: 'center', padding: '7px 12px', borderTop: '0.5px solid var(--aj-border)', cursor: 'grab' }}
    >
      <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} onBlur={commitName} />
      <select style={inputStyle} value={room.roomTemplateId || ''} onChange={e => onUpdate({ 'Room Template': e.target.value ? [e.target.value] : [] })}>
        <option value="">{ROOM_TYPE_PLACEHOLDER}</option>
        {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>
      <input style={inputStyle} type="number" placeholder="SF" value={area} onChange={e => setArea(e.target.value)} onBlur={commitArea} />
      <input style={inputStyle} placeholder="Notes" value={notes} onChange={e => setNotes(e.target.value)} onBlur={commitNotes} />
      <button onClick={onDelete} style={{ ...btnGhost, color: '#c0392b' }}>Delete</button>
    </div>
  )
}

function SuggestionsPanel({ suggestions, busy, onReview }) {
  if (suggestions == null) return <div style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading…</div>
  if (!suggestions.length) return <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No client suggestions yet.</div>
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {suggestions.map(s => (
        <div key={s.id} style={{ border: '0.5px solid var(--aj-border)', borderRadius: 6, padding: '10px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)', textTransform: 'capitalize' }}>{s.action} room</span>
            <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10.5, color: s.status === 'pending' ? '#c07830' : (s.status === 'approved' ? '#1D9E75' : '#c0392b') }}>{s.status}</span>
          </div>
          <SuggestionDiff s={s} />
          {s.reason && <div style={{ fontSize: 11.5, color: 'var(--aj-text-mid)', marginTop: 6, fontStyle: 'italic' }}>"{s.reason}"</div>}
          <div style={{ fontSize: 10.5, color: 'var(--aj-text-muted)', marginTop: 6 }}>
            Suggested by {s.journey_recipients?.name || 'a client'} · {new Date(s.created_at).toLocaleDateString()}
          </div>
          {s.status === 'pending' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button disabled={busy} onClick={() => onReview(s, 'approve')} style={{ ...btnGhost, background: '#1D9E75', color: '#fff', border: 'none' }}>Approve</button>
              <button disabled={busy} onClick={() => onReview(s, 'reject')} style={{ ...btnGhost, color: '#c0392b' }}>Reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function SuggestionDiff({ s }) {
  const before = s.original_snapshot || {}
  const after = { ...before, ...(s.proposed || {}) }
  const rows = []
  if (before.customName !== after.customName) rows.push(['Name', before.customName, after.customName])
  if (before.levelName !== after.levelName) rows.push(['Level', before.levelName, after.levelName])
  if (before.roomTemplateName !== after.roomTemplateName) rows.push(['Room type', before.roomTemplateName, after.roomTemplateName])
  if (before.areaManual !== after.areaManual) rows.push(['Approx. size', before.areaManual, after.areaManual])
  if (before.notes !== after.notes) rows.push(['Notes', before.notes, after.notes])
  if (!rows.length) return null
  return (
    <div style={{ fontSize: 11.5, color: 'var(--aj-text-mid)', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {rows.map(([label, b, a]) => (
        <div key={label}><strong>{label}:</strong> {b || '—'} → {a || '—'}</div>
      ))}
    </div>
  )
}
